/**
 * Clamp QC fail routing: which physical pile the operator should place the unit on after a failed inspection.
 */

export type ClampQcFailDispositionPile = "william" | "repairable" | "unrepairable";

const NATURAL_CLOSE_CATCH_LABEL = "Natural close catch";

/** Mechanical failure reasons that go to the repairable pile (housing assembly / bobbin / wire fixes). */
export const CLAMP_QC_REPAIRABLE_FAIL_REASONS: readonly string[] = [
  "Arm binding",
  "Delamination of bobbin wrapper",
  "Teflon coating missing",
  "Trigger block sticking",
  "Wire damage",
  "Missing screws",
];

function normalizeFailReasonLabel(label: string): string {
  return label.trim().toLowerCase();
}

function failReasonLabelsInclude(labels: readonly string[], canonical: string): boolean {
  const target = normalizeFailReasonLabel(canonical);
  return labels.some((label) => normalizeFailReasonLabel(label) === target);
}

function failReasonLabelsIncludeAny(labels: readonly string[], canonicals: readonly string[]): boolean {
  return canonicals.some((canonical) => failReasonLabelsInclude(labels, canonical));
}

export function clampQcFailDispositionPileLabel(pile: ClampQcFailDispositionPile): string {
  if (pile === "william") {
    return "William";
  }
  if (pile === "repairable") {
    return "Repairable";
  }
  return "Unrepairable";
}

export function clampQcFailDispositionToastMessage(pile: ClampQcFailDispositionPile): string {
  const pileLabel = clampQcFailDispositionPileLabel(pile);
  if (pile === "william") {
    return `Failed — place on ${pileLabel} pile (${NATURAL_CLOSE_CATCH_LABEL}).`;
  }
  return `Failed — place on ${pileLabel} pile.`;
}

function physicalLabelIsUnrepairable(label: string): boolean {
  if (failReasonLabelsInclude([label], NATURAL_CLOSE_CATCH_LABEL)) {
    return false;
  }
  return !failReasonLabelsIncludeAny([label], CLAMP_QC_REPAIRABLE_FAIL_REASONS);
}

/**
 * Resolves the post-fail pile from mechanical reasons and non-mechanical fail signals.
 * Any unrepairable signal wins. Natural close catch goes to William only when nothing is unrepairable.
 * Repairable is used when every selected reason is repairable.
 */
export function resolveClampQcFailDispositionPile(input: {
  physicalFailReasonLabels: readonly string[];
  lineHarvestFailed: boolean;
  inductanceFailed: boolean;
  dragAutoFailed: boolean;
}): ClampQcFailDispositionPile {
  const hasUnrepairableMechanical = input.physicalFailReasonLabels.some((label) =>
    physicalLabelIsUnrepairable(label),
  );
  if (
    hasUnrepairableMechanical ||
    input.lineHarvestFailed ||
    input.inductanceFailed ||
    input.dragAutoFailed
  ) {
    return "unrepairable";
  }
  if (failReasonLabelsInclude(input.physicalFailReasonLabels, NATURAL_CLOSE_CATCH_LABEL)) {
    return "william";
  }
  if (failReasonLabelsIncludeAny(input.physicalFailReasonLabels, CLAMP_QC_REPAIRABLE_FAIL_REASONS)) {
    return "repairable";
  }
  return "unrepairable";
}

/** True when the operator selected one or more mechanical fail reasons (skips drag and inductance entry). */
export function clampQcMechanicalFailureSkipsOtherTests(physicalFailReasonLabels: readonly string[]): boolean {
  return physicalFailReasonLabels.length > 0;
}
